import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    DynamicComponentExitReason,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { TmDialogService } from '@treasury/presentation/services/dialog/tm-dialog.service';
import { type TmDialogServiceConfig } from '@treasury/presentation/services/dialog/tm-dialog.service.types';
import { exists } from '@treasury/utils';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { OmegaDialog } from '../../components/omega-dialog';
import { normalizeOptions } from './omega-dialog.normalization';
import { mapConfig } from './omega-dialog.service.mappings';
import {
    OmegaDialogOptions,
    OmegaDialogOptionsAll,
    postureToTypeMap,
} from './omega-dialog.service.types';

import '../../components/omega-dialog';
import '../../components/omega-dialog-branded';

/**
 * Dialog options not covered by `TmDialogServiceConfig`.
 */
type UniqueDialogOptions = Omit<OmegaDialogOptions, 'preventClose' | 'renderButtons' | 'host'>;

/**
 * Service for rendering arbitrary content into an `<omega-dialog>`.
 */
@Injectable()
export class OmegaDialogService extends TmDialogService<OmegaDialog> {
    private contentElement?: Element;

    public open<T = unknown>(
        content: string | TemplateResult,
        title: string,
        config: TmDialogServiceConfig<T> = {},
        /**
         * Backward compatibility for config that doesn't directly translate to `TmDialogServiceConfig`.
         */
        options?: UniqueDialogOptions
    ) {
        const allOptions = this.mapAndNormalizeOptions(config, options);
        const close = (result?: DynamicComponentResult<T>) => {
            const { reason, data } = result || {};
            this.close(reason, data);
        };
        const template = this.renderDialog<T>(content, title, allOptions, close);
        const handle = this.registry.create<T>(template, allOptions);
        const { element, listenFor } = handle;

        this.handle = handle;
        this.contentElement = this.getDialogContentElement(element);

        // listen for close events raised by the element inside
        // the dialog since it may not own its buttons
        listenFor<DynamicComponentResult<T>>('close', ({ detail }) => close(detail));

        return handle.disposed;
    }

    public getContent<E extends Element = Element>() {
        if (!this.contentElement) {
            throw new Error('Could not get dialog content. No open dialog.');
        }

        return this.contentElement as E;
    }

    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }

        const { element } = this.handle;
        if (element.open) {
            element.open = false;
        }

        this.contentElement = undefined;
        // allow time for animation
        await super.close(reason, data);
    }

    private getDialogContentElement<Elem extends Element>(dialogElement: OmegaDialog) {
        const contentContainer = dialogElement.querySelector('.dialog-content');

        if (!contentContainer) {
            throw new Error('Could not get dialog content element.');
        }

        if (!contentContainer.firstElementChild) {
            throw new Error(
                'Could not get dialog content element. Did you pass a template containing text with no parent element?'
            );
        }

        return contentContainer.firstElementChild as Elem;
    }

    private renderButtons<T>(
        buttons: OmegaDialogOptionsAll['buttons'],
        options: OmegaDialogOptionsAll,
        closeDialog: (result: DynamicComponentResult<T>) => void
    ) {
        const { preventClose, renderButtons } = options;

        if (!renderButtons) {
            return nothing;
        }

        const buttonElements: TemplateResult[] = [];
        const confirmBtn = buttons[DynamicComponentExitReason.Confirm];
        const cancelBtn = buttons[DynamicComponentExitReason.Cancel];

        if (exists(confirmBtn)) {
            buttonElements.push(
                html` <omega-button
                    slot="actions"
                    type=${postureToTypeMap[confirmBtn.posture]}
                    @click=${() =>
                        closeDialog({
                            reason: DynamicComponentExitReason.Confirm,
                        })}
                    >${confirmBtn.label}</omega-button
                >`
            );
        }

        if (exists(cancelBtn)) {
            buttonElements.push(
                html` <omega-button
                    slot="actions"
                    .disabled=${preventClose}
                    type=${postureToTypeMap[cancelBtn.posture]}
                    @click=${() =>
                        closeDialog({
                            reason: DynamicComponentExitReason.Cancel,
                        })}
                    >${cancelBtn.label}</omega-button
                >`
            );
        }

        return buttonElements;
    }

    private renderSubheader(subHeader?: string) {
        if (!subHeader) {
            return nothing;
        }
        return html`<div
            slot="sub-header"
            class="sub-header"
            style="padding-left: 16px; margin-top: -12px"
        >
            ${subHeader}
        </div>`;
    }

    private renderDialog<T>(
        content: string | TemplateResult,
        title: string,
        options: OmegaDialogOptionsAll,
        closeDialog: (result: DynamicComponentResult<T>) => void
    ) {
        const {
            buttons,
            preventClose,
            logoSource,
            height,
            heightUnits,
            width,
            widthUnits,
            headerIcon,
            subHeader,
        } = options;

        const contentTemplate = typeof content === 'string' ? html`<p>${content}</p>` : content;

        if (logoSource) {
            return html` <omega-dialog-branded
                .hideCloseButton=${preventClose}
                @close=${(e: CustomEvent<DynamicComponentResult<T>>) => closeDialog(e.detail)}
                dialogTitle="${title}"
                logoSource=${logoSource}
                open
            >
                ${this.renderSubheader(subHeader)}
                <div
                    class="dialog-content"
                    slot="content"
                    style=${`padding: 20px; min-width: 80%; max-width: 100%; width: ${width}${widthUnits}
                    ; height: ${height}${heightUnits}; min-height: 80%;`}
                >
                    ${contentTemplate}
                </div>
                ${this.renderButtons(buttons, options, closeDialog)}
            </omega-dialog-branded>`;
        }

        return html` <omega-dialog
            .hideCloseButton=${preventClose}
            @close=${(e: CustomEvent<DynamicComponentResult<T>>) => closeDialog(e.detail)}
            dialogTitle="${title}"
            headerIcon=${ifDefined(headerIcon)}
            open
        >
            ${this.renderSubheader(subHeader)}
            <div
                class="dialog-content"
                slot="content"
                style=${`padding: 20px; min-width: 80%;  max-width: 100%; width: ${width}${widthUnits}; height: ${height}${heightUnits}; min-height: 80%;`}
            >
                ${contentTemplate}
            </div>
            ${this.renderButtons(buttons, options, closeDialog)}
        </omega-dialog>`;
    }

    /**
     * Convert and normalize a `TmDialogServiceConfig` into an `OmegaDialogOptions`.
     */
    private mapAndNormalizeOptions<T>(
        config?: TmDialogServiceConfig<T>,
        options?: OmegaDialogOptions
    ) {
        const normalized = this.normalizeConfig<T>(config);
        const mapped = mapConfig<T>(normalized);
        return normalizeOptions({
            ...mapped,
            ...options,
        });
    }
}
